import { useAccount, useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type Dispatch, type PointerEvent as ReactPointerEvent, type SetStateAction } from "react";
import logoUrl from "../../imgs/logo_full_color_white_text.svg";
import { ApiError } from "../api";
import { LoginScreen } from "../auth/AuthGate";
import { accountName, acquireIdToken } from "../auth/token";
import { findTeamMember } from "../floor/teamColors";
import { ContactCard, type SaveStatus } from "./ContactCard";
import { CompanyBrowserScreen } from "../company-browser/CompanyBrowserScreen";
import {
  DEBRIEF_EVENTS,
  cardsForEvent,
  cardsMatchingOwner,
  contactIdLabel,
  dotWindow,
  eventByKey,
  flagsKnown,
  initials,
  latestFollowup,
  latestNote,
  prioritizedCards,
  progressCounts,
  searchCards,
  wrapIndex,
  type CompanyNameSort,
} from "./logic";
import { addContactToShow, hydrateContact, loadAccountContacts, loadIntelDebrief, mergeSearchRoster, refreshBusinessCard, saveDebriefPatch, searchDebriefContacts, unionSearchRoster } from "./intelDebrief";
import { loadReviewedIds, saveReviewedIds } from "./store";
import type { DebriefCard, DebriefPatch, DebriefView } from "./types";
import "./debrief.css";

const VIEW_KEY = "ironfleet-debrief:view";
const EVENT_KEY = "ironfleet-debrief:event";
const LIST_WIDTH_KEY = "ironfleet-debrief:list-width";
const LIST_HEIGHT_KEY = "ironfleet-debrief:list-height";
const PROFILE_RADIUS = 3;
const STACKED_QUERY = "(max-width: 600px)";

function readStoredPx(key: string, fallback: number): number {
  const raw = Number(localStorage.getItem(key));
  return Number.isFinite(raw) && raw > 0 ? raw : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function readView(): DebriefView {
  const fromUrl = new URLSearchParams(window.location.search).get("view");
  if (fromUrl === "list" || fromUrl === "carousel" || fromUrl === "prioritized") return fromUrl;
  const stored = localStorage.getItem(VIEW_KEY);
  if (stored === "list" || stored === "prioritized") return stored;
  return "carousel";
}

function paintHydration(
  setCards: Dispatch<SetStateAction<DebriefCard[]>>,
  contactId: string,
  accountId: string,
  patch: Partial<DebriefCard>,
  dirtyIds: Set<string>,
) {
  if (dirtyIds.has(contactId)) {
    if (!patch.photoUrl && !patch.businessCardUrl && !patch.businessCardFrontUrl && !patch.businessCardBackUrl) return;
    setCards((prev) =>
      prev.map((item) =>
        item.contactId === contactId
          ? {
              ...item,
              photoUrl: patch.photoUrl ?? item.photoUrl,
              businessCardUrl: item.businessCardUrl ?? patch.businessCardUrl ?? null,
              businessCardFrontUrl: item.businessCardFrontUrl ?? patch.businessCardFrontUrl ?? null,
              businessCardBackUrl: item.businessCardBackUrl ?? patch.businessCardBackUrl ?? null,
            }
          : item,
      ),
    );
    return;
  }
  setCards((prev) =>
    prev.map((item) => {
      if (item.contactId === contactId) {
        return {
          ...item,
          ...patch,
          photoUrl: patch.photoUrl ?? item.photoUrl,
          businessCardUrl: item.businessCardUrl ?? patch.businessCardUrl ?? null,
          businessCardFrontUrl: item.businessCardFrontUrl ?? patch.businessCardFrontUrl ?? null,
          businessCardBackUrl: item.businessCardBackUrl ?? patch.businessCardBackUrl ?? null,
        };
      }
      if (patch.priorityIds && accountId && item.accountId === accountId && item.priorityIds.length === 0) {
        return { ...item, priorityIds: patch.priorityIds };
      }
      if (patch.rating !== undefined && accountId && item.accountId === accountId && !dirtyIds.has(item.contactId)) {
        return { ...item, rating: patch.rating };
      }
      if (patch.priorityFollowup !== undefined && accountId && item.accountId === accountId && !dirtyIds.has(item.contactId)) {
        return { ...item, priorityFollowup: patch.priorityFollowup };
      }
      if (patch.ownerUserId !== undefined && accountId && item.accountId === accountId && !dirtyIds.has(item.contactId)) {
        return { ...item, ownerUserId: patch.ownerUserId };
      }
      return item;
    }),
  );
}

function ownerLabel(userId: string | null | undefined): string {
  if (!userId) return "—";
  return findTeamMember(userId)?.name ?? userId;
}

function ownerKey(userId: string | null | undefined): string {
  const raw = userId?.trim() ?? "";
  if (!raw) return "";
  return (findTeamMember(raw)?.email ?? raw).toLowerCase();
}

function dueLabel(dueAt: string | null): string {
  if (!dueAt) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueAt);
  if (!match) return dueAt;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" }).format(date);
}

function readEvent(): string {
  const fromUrl = new URLSearchParams(window.location.search).get("event");
  if (fromUrl && DEBRIEF_EVENTS.some((event) => event.eventKey === fromUrl)) return fromUrl;
  const stored = localStorage.getItem(EVENT_KEY);
  if (stored && DEBRIEF_EVENTS.some((event) => event.eventKey === stored)) return stored;
  return DEBRIEF_EVENTS[0].eventKey;
}

function needsSignIn(error: unknown): boolean {
  if (error instanceof ApiError && error.status === 401) return true;
  const message = error instanceof Error ? error.message : "";
  return /sign in|not signed in|unauthorized/i.test(message);
}

export function DebriefScreen() {
  const { instance, accounts } = useMsal();
  const activeAccount = useAccount();
  const account = activeAccount ?? accounts[0] ?? null;
  const currentUserId = account?.username?.trim() || "";
  const currentUserName = accountName(account) || "";
  const [cards, setCards] = useState<DebriefCard[]>([]);
  const [loading, setLoading] = useState(Boolean(currentUserId));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [view, setView] = useState<DebriefView>(() => readView());
  const [eventKey, setEventKey] = useState(() => readEvent());
  const [rawIndex, setIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [companySort, setCompanySort] = useState<CompanyNameSort>("asc");
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null);
  const [ownerMenuOpen, setOwnerMenuOpen] = useState(false);
  const ownerMenuRef = useRef<HTMLDivElement | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const [savedAt, setSavedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [accountModalId, setAccountModalId] = useState<string | null>(null);
  const [listWidth, setListWidth] = useState(() => readStoredPx(LIST_WIDTH_KEY, 320));
  const [listHeight, setListHeight] = useState(() => readStoredPx(LIST_HEIGHT_KEY, 280));
  const [stacked, setStacked] = useState(() => window.matchMedia(STACKED_QUERY).matches);
  const [draggingSplit, setDraggingSplit] = useState(false);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const layoutRef = useRef<HTMLElement | null>(null);
  const saveTimer = useRef<number | null>(null);
  const lastSave = useRef<{ card: DebriefCard; patch: DebriefPatch } | null>(null);
  const pointer = useRef<{
    id: number;
    x: number;
    y: number;
    locked: "x" | "y" | null;
    ignore: boolean;
  } | null>(null);
  const stageWrapRef = useRef<HTMLElement | null>(null);

  const showCards = useMemo(() => cardsForEvent(cards, eventKey), [cards, eventKey]);
  const index = showCards.length === 0 ? 0 : wrapIndex(rawIndex, showCards.length);
  const active = showCards[index] ?? null;
  const counts = progressCounts(showCards);
  const listRows = useMemo(() => searchCards(cards, query, eventKey, showAll), [cards, query, eventKey, showAll]);
  const priorityBase = useMemo(
    () => prioritizedCards(cards, query, eventKey, showAll, companySort),
    [cards, query, eventKey, showAll, companySort],
  );
  const ownerChoices = useMemo(() => {
    const byId = new Map<string, string>();
    for (const card of priorityBase) {
      const id = ownerKey(card.ownerUserId);
      if (!byId.has(id)) byId.set(id, id ? ownerLabel(card.ownerUserId) : "Unassigned");
    }
    return [...byId.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [priorityBase]);
  const priorityRows = useMemo(
    () => cardsMatchingOwner(priorityBase, ownerFilter, (card) => ownerKey(card.ownerUserId)),
    [priorityBase, ownerFilter],
  );
  const priorityCandidates = useMemo(() => searchCards(cards, query, eventKey, false), [cards, query, eventKey]);
  const priorityPending = priorityCandidates.filter((card) => !flagsKnown(card)).length;
  const priorityProbeKey = useMemo(
    () =>
      priorityCandidates
        .filter((card) => !flagsKnown(card))
        .map((card) => card.contactId)
        .sort()
        .join("\n"),
    [priorityCandidates],
  );
  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  const nameByUser = useMemo(() => {
    const names = new Map<string, string>([[currentUserId, currentUserName]]);
    for (const card of cards) {
      for (const note of card.notes) {
        if (note.authorId && note.authorName) names.set(note.authorId, note.authorName);
      }
    }
    return names;
  }, [cards, currentUserId, currentUserName]);

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!ownerMenuOpen) return;
    function onPointer(event: PointerEvent) {
      if (!ownerMenuRef.current?.contains(event.target as Node)) setOwnerMenuOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOwnerMenuOpen(false);
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [ownerMenuOpen]);

  useEffect(() => {
    const media = window.matchMedia(STACKED_QUERY);
    const onChange = () => setStacked(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    localStorage.setItem(LIST_WIDTH_KEY, String(listWidth));
  }, [listWidth]);

  useEffect(() => {
    localStorage.setItem(LIST_HEIGHT_KEY, String(listHeight));
  }, [listHeight]);

  const splitBounds = useCallback(() => {
    const layout = layoutRef.current;
    if (!layout) return { min: 160, max: 480 };
    const bounds = layout.getBoundingClientRect();
    if (window.matchMedia(STACKED_QUERY).matches) {
      return { min: 120, max: Math.max(160, bounds.height - 160) };
    }
    return { min: 220, max: Math.max(280, bounds.width - 280) };
  }, []);

  const nudgeSplit = useCallback(
    (delta: number) => {
      const { min, max } = splitBounds();
      if (window.matchMedia(STACKED_QUERY).matches) setListHeight((size) => clamp(size + delta, min, max));
      else setListWidth((size) => clamp(size + delta, min, max));
    },
    [splitBounds],
  );

  function onSplitPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const vertical = window.matchMedia(STACKED_QUERY).matches;
    const origin = vertical ? event.clientY : event.clientX;
    const start = vertical ? listHeight : listWidth;
    const { min, max } = splitBounds();
    setDraggingSplit(true);
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = vertical ? "row-resize" : "col-resize";

    const onMove = (ev: PointerEvent) => {
      const next = Math.round(start + (vertical ? ev.clientY : ev.clientX) - origin);
      const size = clamp(next, min, max);
      if (vertical) setListHeight(size);
      else setListWidth(size);
    };
    const onUp = () => {
      setDraggingSplit(false);
      document.body.style.cursor = previousCursor;
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  }

  useEffect(() => {
    const url = new URL(window.location.href);
    url.pathname = "/debrief";
    url.searchParams.set("event", eventKey);
    url.searchParams.set("view", view);
    window.history.replaceState({ debrief: true }, "", url);
    localStorage.setItem(VIEW_KEY, view);
    localStorage.setItem(EVENT_KEY, eventKey);
  }, [eventKey, view]);

  useEffect(() => {
    if (view !== "carousel") return;
    headingRef.current?.focus({ preventScroll: true });
    stageWrapRef.current?.scrollTo(0, 0);
  }, [index, view, active?.contactId]);

  const reviewedRef = useRef(loadReviewedIds());
  const accountExtras = useRef<DebriefCard[]>([]);
  const hydrated = useRef(new Set<string>());
  const inflight = useRef(new Set<string>());
  const dirty = useRef(new Set<string>());

  useEffect(() => {
    if (!currentUserId) {
      setLoading(false);
      setCards([]);
      setAuthRequired(true);
      return;
    }
    let cancelled = false;
    setAuthRequired(false);
    setLoading(true);
    setLoadError(null);
    hydrated.current.clear();
    accountExtras.current = [];
    let sawBatch = false;
    const paint = (rows: DebriefCard[]) => {
      setCards((prev) => unionSearchRoster(mergeSearchRoster(prev, rows, hydrated.current), accountExtras.current, hydrated.current));
    };
    acquireIdToken(instance)
      .then(() =>
        loadIntelDebrief(eventByKey(eventKey).campaignKey, reviewedRef.current, (rows) => {
          if (cancelled) return;
          sawBatch = true;
          paint(rows);
          setLoading(false);
        }),
      )
      .then((rows) => {
        if (cancelled) return;
        sawBatch = true;
        paint(rows);
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (needsSignIn(error)) {
          setCards([]);
          setAuthRequired(true);
          setLoadError(null);
        } else {
          if (!sawBatch) setCards([]);
          setLoadError(error instanceof Error ? error.message : "Intel contact load failed");
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserId, eventKey, instance]);

  useEffect(() => {
    const accountId = active?.accountId;
    if (!accountId || !currentUserId) return;
    let cancelled = false;
    loadAccountContacts(accountId, eventByKey(eventKey).campaignKey, reviewedRef.current)
      .then((rows) => {
        if (cancelled) return;
        accountExtras.current = rows;
        setCards((prev) => unionSearchRoster(prev, rows, hydrated.current));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [active?.accountId, currentUserId, eventKey]);

  useEffect(() => {
    const needle = query.trim();
    if (!showAll || needle.length < 2 || !currentUserId) return;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      searchDebriefContacts(eventByKey(eventKey).campaignKey, needle, reviewedRef.current)
        .then((rows) => {
          if (cancelled) return;
          setCards((prev) => unionSearchRoster(prev, rows, hydrated.current));
        })
        .catch(() => undefined);
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [showAll, query, currentUserId, eventKey]);

  useEffect(() => {
    if (showCards.length === 0) return;
    const center = Math.min(index, showCards.length - 1);
    let furthestRight = -1;
    for (let slot = 0; slot < showCards.length; slot += 1) {
      const id = showCards[slot]?.contactId;
      if (id && (hydrated.current.has(id) || inflight.current.has(id))) furthestRight = slot;
    }
    let right = Math.min(showCards.length - 1, center + PROFILE_RADIUS);
    if (furthestRight >= 0 && center >= furthestRight - 1) {
      right = Math.min(showCards.length - 1, Math.max(right, furthestRight + PROFILE_RADIUS));
    }
    const left = Math.max(0, center - PROFILE_RADIUS);
    const windowCards = showCards.slice(left, right + 1);
    const current = showCards[center];
    const ordered = current ? [current, ...windowCards.filter((card) => card.contactId !== current.contactId)] : windowCards;
    for (const card of ordered) {
      if (hydrated.current.has(card.contactId) || inflight.current.has(card.contactId)) continue;
      inflight.current.add(card.contactId);
      const contactId = card.contactId;
      const accountId = card.accountId;
      hydrateContact(card, eventKey)
        .then((patch) => {
          inflight.current.delete(contactId);
          hydrated.current.add(contactId);
          paintHydration(setCards, contactId, accountId, patch, dirty.current);
        })
        .catch(() => {
          inflight.current.delete(contactId);
        });
    }
  }, [eventKey, index, showCards]);

  const priorityProbeKeyStable = priorityProbeKey;

  useEffect(() => {
    if (view !== "prioritized" || !priorityProbeKeyStable) return;
    const queue = priorityProbeKeyStable.split("\n");
    let cursor = 0;
    async function worker() {
      while (cursor < queue.length) {
        const contactId = queue[cursor];
        cursor += 1;
        if (!contactId || inflight.current.has(contactId)) continue;
        const card = cardsRef.current.find((item) => item.contactId === contactId);
        if (!card || flagsKnown(card)) continue;
        inflight.current.add(contactId);
        try {
          const patch = await hydrateContact(card, eventKey);
          if (patch.keyContact === undefined) patch.keyContact = false;
          if (patch.priorityFollowup === undefined) patch.priorityFollowup = false;
          hydrated.current.add(contactId);
          paintHydration(setCards, contactId, card.accountId, patch, dirty.current);
        } catch {
          // Leave the flags unknown so a later visit can retry.
        } finally {
          inflight.current.delete(contactId);
        }
      }
    }
    const workers = Math.min(6, queue.length);
    for (let slot = 0; slot < workers; slot += 1) void worker();
  }, [view, priorityProbeKeyStable, eventKey]);

  useEffect(() => {
    const contactId = active?.contactId;
    if (!contactId || !currentUserId) return;
    let cancelled = false;
    refreshBusinessCard(contactId, eventKey).then((patch) => {
      if (cancelled || !patch) return;
      setCards((prev) =>
        prev.map((item) => {
          if (item.contactId !== contactId) return item;
          if (
            item.businessCardUrl === patch.businessCardUrl &&
            item.businessCardFrontUrl === patch.businessCardFrontUrl &&
            item.businessCardBackUrl === patch.businessCardBackUrl
          ) {
            return item;
          }
          return { ...item, ...patch };
        }),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [active?.contactId, currentUserId, eventKey]);

  const flush = useCallback((card: DebriefCard, patch: DebriefPatch, delay: number) => {
    lastSave.current = { card, patch };
    setSaveStatus(navigator.onLine ? "saving" : "offline");
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      if (!navigator.onLine) {
        setSaveStatus("offline");
        return;
      }
      saveDebriefPatch(card, patch, currentUserId, eventKey)
        .then((applied) => {
          setCards((prev) =>
            prev.map((item) => {
              if (item.contactId === card.contactId) return { ...item, ...applied };
              if (applied.priorityIds !== undefined && item.accountId && item.accountId === card.accountId) {
                return { ...item, priorityIds: applied.priorityIds };
              }
              if (patch.segment !== undefined && item.accountId && item.accountId === card.accountId) {
                return { ...item, segment: patch.segment };
              }
              if (patch.rating !== undefined && item.accountId && item.accountId === card.accountId) {
                return { ...item, rating: patch.rating };
              }
              if (applied.priorityFollowup !== undefined && item.accountId && item.accountId === card.accountId) {
                return { ...item, priorityFollowup: applied.priorityFollowup };
              }
              if (applied.ownerUserId !== undefined && item.accountId && item.accountId === card.accountId) {
                return { ...item, ownerUserId: applied.ownerUserId };
              }
              return item;
            }),
          );
          setSavedAt(Date.now());
          setSaveStatus("saved");
          dirty.current.delete(card.contactId);
        })
        .catch(() => setSaveStatus("error"));
    }, delay);
  }, [currentUserId, eventKey]);

  const flushPending = useCallback(() => {
    const pending = lastSave.current;
    if (!pending || saveTimer.current == null) return;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = null;
    flush(pending.card, pending.patch, 0);
  }, [flush]);

  function patchActive(patch: DebriefPatch, immediate: boolean) {
    if (!active) return;
    if (patch.reviewed) {
      reviewedRef.current.add(active.contactId);
      saveReviewedIds(reviewedRef.current);
    }
    const serverPatch = { ...patch };
    delete serverPatch.reviewed;
    const next = cards.map((card) => {
      if (card.contactId === active.contactId) return { ...card, ...patch, updatedAt: new Date().toISOString() };
      if (patch.priorityIds !== undefined && card.accountId && card.accountId === active.accountId) {
        return { ...card, priorityIds: patch.priorityIds };
      }
      if (patch.segment !== undefined && card.accountId && card.accountId === active.accountId) {
        return { ...card, segment: patch.segment };
      }
      if (patch.rating !== undefined && card.accountId && card.accountId === active.accountId) {
        return { ...card, rating: patch.rating };
      }
      if (patch.priorityFollowup !== undefined && card.accountId && card.accountId === active.accountId) {
        return { ...card, priorityFollowup: patch.priorityFollowup };
      }
      if (patch.ownerUserId !== undefined && card.accountId && card.accountId === active.accountId) {
        return { ...card, ownerUserId: patch.ownerUserId };
      }
      return card;
    });
    setCards(next);
    const hasServerWrite =
      serverPatch.notes != null ||
      serverPatch.followups != null ||
      serverPatch.priorityIds != null ||
      serverPatch.segment !== undefined ||
      serverPatch.rating !== undefined ||
      serverPatch.familiarUserIds != null ||
      serverPatch.priorityFollowup !== undefined ||
      serverPatch.keyContact !== undefined ||
      serverPatch.ownerUserId !== undefined;
    if (!hasServerWrite) {
      setSavedAt(Date.now());
      setSaveStatus("saved");
      return;
    }
    dirty.current.add(active.contactId);
    const base = cards.find((card) => card.contactId === active.contactId) ?? active;
    flush(base, serverPatch, immediate ? 150 : 800);
  }

  function addSibling(contactId: string) {
    const event = eventByKey(eventKey);
    setSaveStatus(navigator.onLine ? "saving" : "offline");
    addContactToShow(contactId, event.campaignKey, currentUserId)
      .then((assigned) => {
        if (assigned.eventContactId == null) {
          setLoadError("Added to the show, but Intel did not assign an Event Contact ID.");
          setSaveStatus("error");
          return;
        }
        setCards((prev) => {
          const next = prev.map((card) =>
            card.contactId === contactId
              ? {
                  ...card,
                  eventKey: event.eventKey,
                  eventContactId: assigned.eventContactId,
                  eventContactDisplay: assigned.eventContactDisplay,
                  campaignKey: event.campaignKey,
                  updatedAt: new Date().toISOString(),
                }
              : card,
          );
          const placed = cardsForEvent(next, eventKey).findIndex((card) => card.contactId === contactId);
          if (placed >= 0) setIndex(placed);
          return next;
        });
        setSavedAt(Date.now());
        setSaveStatus("saved");
      })
      .catch((error: unknown) => {
        setLoadError(error instanceof Error ? error.message : "campaign_journey_put failed");
        setSaveStatus("error");
      });
  }

  function openPrioritized(card: DebriefCard) {
    openFromList(card);
    setView("carousel");
  }

  function openFromList(card: DebriefCard) {
    if (card.eventKey !== eventKey || card.eventContactId == null) return;
    const placed = showCards.findIndex((item) => item.contactId === card.contactId);
    if (placed >= 0) {
      flushPending();
      setIndex(placed);
    }
  }

  function go(delta: number) {
    if (showCards.length <= 1) return;
    flushPending();
    setIndex((current) => wrapIndex(current + delta, showCards.length));
  }

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (view !== "carousel") return;
      const target = event.target as HTMLElement | null;
      const typing = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (event.key === "ArrowLeft" && !typing) {
        event.preventDefault();
        go(-1);
      } else if (event.key === "ArrowRight" && !typing) {
        event.preventDefault();
        go(1);
      } else if (event.key === "Home" && !typing) {
        if (index !== 0) {
          flushPending();
          setIndex(0);
        }
      } else if (event.key === "End" && !typing) {
        const last = Math.max(showCards.length - 1, 0);
        if (index !== last) {
          flushPending();
          setIndex(last);
        }
      } else if (!typing && active && /^[0-5]$/.test(event.key)) {
        patchActive({ rating: Number(event.key) as DebriefPatch["rating"], reviewed: true }, true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const looping = showCards.length > 1;
  const prev = looping ? showCards[wrapIndex(index - 1, showCards.length)] : null;
  const next = looping ? showCards[wrapIndex(index + 1, showCards.length)] : null;
  const dots = dotWindow(index, showCards.length);
  const siblings = active ? cards.filter((card) => card.accountId === active.accountId && card.contactId !== active.contactId) : [];

  function debriefProfile(card: DebriefCard, position: number) {
    return (
      <ContactCard
        card={card}
        siblings={siblings}
        position={position}
        total={showCards.length}
        currentUserId={currentUserId}
        nameByUser={nameByUser}
        saveStatus={saveStatus}
        savedAt={savedAt}
        now={now}
        onPatch={patchActive}
        onRetry={() => {
          const last = lastSave.current;
          if (last) flush(last.card, last.patch, 0);
        }}
        onAddSibling={addSibling}
        onOpenAccount={setAccountModalId}
        onPhotoNav={(delta) => go(delta)}
        headingRef={(node) => {
          headingRef.current = node;
        }}
      />
    );
  }

  if (!currentUserId || authRequired) {
    return <LoginScreen />;
  }

  return (
    <div className="df-app">
      <header className="df-bar">
        <a className="df-logo" href="/">
          <img src={logoUrl} alt="Ironfleet" />
        </a>
        <div className="df-center">
          <div className="df-toggle" role="group" aria-label="Debrief view">
            <button
              type="button"
              className={view === "carousel" ? "on" : ""}
              aria-pressed={view === "carousel"}
              onClick={() => setView("carousel")}
            >
              <span className="df-wide">Carousel</span>
              <span className="df-narrow">Cards</span>
            </button>
            <button type="button" className={view === "list" ? "on" : ""} aria-pressed={view === "list"} onClick={() => setView("list")}>
              List
            </button>
            <button
              type="button"
              className={view === "prioritized" ? "on" : ""}
              aria-pressed={view === "prioritized"}
              onClick={() => setView("prioritized")}
            >
              Prioritized
            </button>
          </div>
          <label className="df-event">
            <span className="df-sr">Tradeshow</span>
            <select
              value={eventKey}
              onChange={(event) => {
                flushPending();
                setEventKey(event.target.value);
                setIndex(0);
              }}
            >
              {DEBRIEF_EVENTS.map((item) => (
                <option key={item.eventKey} value={item.eventKey}>
                  {stacked ? item.shortLabel : item.label}
                </option>
              ))}
            </select>
            <small>{counts.total} contacts</small>
          </label>
        </div>
        {loadError ? <p className="df-load-error">{loadError}</p> : null}
        <p className="df-progress">
          {stacked
            ? `${counts.reviewed}/${counts.total}`
            : view === "carousel"
              ? `${counts.reviewed} of ${counts.total} reviewed · ${counts.unrated} unrated`
              : `${view === "prioritized" ? priorityRows.length : listRows.length} contacts`}
        </p>
      </header>
      {view === "carousel" ? (
        <MobileNavStrip
          index={showCards.length === 0 ? 0 : index}
          total={showCards.length}
          onPrev={() => go(-1)}
          onNext={() => go(1)}
          onJump={(next) => {
            if (next === index) return;
            flushPending();
            setIndex(next);
          }}
        />
      ) : null}

      {view === "prioritized" ? (
        <main className="df-priority-view">
          <SearchRow query={query} showAll={showAll} onQuery={setQuery} onShowAll={setShowAll} />
          {priorityPending > 0 ? (
            <p className="df-priority-status">
              Loading prioritized contacts… {priorityCandidates.length - priorityPending} of {priorityCandidates.length}
            </p>
          ) : null}
          <div className="df-priority-scroll">
              <table className="df-priority-table">
                <thead>
                  <tr>
                    <th className="df-priority-photo-col">
                      <span className="df-sr">Photo</span>
                    </th>
                    <th>
                      <span className="df-priority-contact-head">
                        Contact
                        <button
                          type="button"
                          className="df-priority-sort"
                          aria-label={companySort === "asc" ? "Sort company name descending" : "Sort company name ascending"}
                          onClick={() => setCompanySort((current) => (current === "asc" ? "desc" : "asc"))}
                        >
                          Company {companySort === "asc" ? "↑" : "↓"}
                        </button>
                      </span>
                    </th>
                    <th>Last note</th>
                    <th>Next follow-up</th>
                    <th>
                      <div className="df-priority-owner-head" ref={ownerMenuRef}>
                        <span>Owner</span>
                        <button
                          type="button"
                          className={ownerFilter == null ? "df-priority-owner-filter" : "df-priority-owner-filter is-on"}
                          aria-haspopup="listbox"
                          aria-expanded={ownerMenuOpen}
                          aria-label={
                            ownerFilter == null
                              ? "Filter by owner"
                              : `Filtered to ${ownerChoices.find((choice) => choice.id === ownerFilter)?.label ?? "owner"}`
                          }
                          onClick={() => setOwnerMenuOpen((open) => !open)}
                        >
                          <span aria-hidden="true">{ownerFilter == null ? "▾" : "●"}</span>
                        </button>
                        {ownerMenuOpen ? (
                          <div className="df-priority-owner-menu" role="listbox" aria-label="Owners">
                            <button
                              type="button"
                              role="option"
                              aria-selected={ownerFilter == null}
                              onClick={() => {
                                setOwnerFilter(null);
                                setOwnerMenuOpen(false);
                              }}
                            >
                              All owners
                            </button>
                            {ownerChoices.map((choice) => (
                              <button
                                key={choice.id || "unassigned"}
                                type="button"
                                role="option"
                                aria-selected={ownerFilter === choice.id}
                                onClick={() => {
                                  setOwnerFilter(choice.id);
                                  setOwnerMenuOpen(false);
                                }}
                              >
                                {choice.label}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </th>
                    <th>Lead rating</th>
                  </tr>
                </thead>
                <tbody>
                  {priorityRows.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        {priorityPending > 0
                          ? `Loading prioritized contacts… ${priorityCandidates.length - priorityPending} of ${priorityCandidates.length}`
                          : ownerFilter != null
                            ? "No key contacts for this owner."
                            : showAll
                              ? "No contacts match this search."
                              : "No key contacts on prioritized accounts."}
                      </td>
                    </tr>
                  ) : null}
                  {priorityRows.map((card) => {
                    const onShow = card.eventKey === eventKey && card.eventContactId != null;
                    const note = latestNote(card);
                    const follow = latestFollowup(card);
                    const followWhen = follow ? dueLabel(follow.dueAt) : "";
                    return (
                      <tr
                        key={card.contactId}
                        className={onShow ? "df-priority-row" : undefined}
                        onClick={onShow ? () => openPrioritized(card) : undefined}
                      >
                        <td>
                          {card.photoUrl ? (
                            <img className="df-priority-photo" src={card.photoUrl} alt="" />
                          ) : (
                            <span className="df-priority-photo df-priority-initials" aria-hidden="true">
                              {initials(card.fullName)}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="df-priority-person">
                            <button type="button" disabled={!onShow} onClick={() => openPrioritized(card)}>
                              {card.fullName}
                            </button>
                            {card.title ? <span>{card.title}</span> : null}
                            {card.accountName ? (
                              <button
                                type="button"
                                className="df-priority-company"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setAccountModalId(card.accountId);
                                }}
                              >
                                {card.accountName}
                              </button>
                            ) : null}
                            {!onShow ? (
                              <button
                                type="button"
                                className="df-add"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  addSibling(card.contactId);
                                }}
                              >
                                Add to show
                              </button>
                            ) : null}
                          </div>
                        </td>
                        <td className="df-priority-clip">{note?.body || "—"}</td>
                        <td className="df-priority-clip">
                          {follow ? (
                            <>
                              {follow.title}
                              {followWhen ? <span className="df-priority-when">{followWhen}</span> : null}
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{ownerLabel(card.ownerUserId)}</td>
                        <td>
                          <span className="df-priority-stars" aria-label={card.rating ? `${card.rating} of 5 stars` : "Unrated"}>
                            {"★★★★★".slice(0, card.rating)}
                            <span className="df-priority-stars-off">{"★★★★★".slice(card.rating)}</span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          </div>
        </main>
      ) : view === "list" ? (
        <main
          ref={layoutRef}
          className={draggingSplit ? "df-list-layout is-dragging" : "df-list-layout"}
          style={{ "--df-list-width": `${listWidth}px`, "--df-list-height": `${listHeight}px` } as CSSProperties}
        >
          <aside className="df-list df-list-col">
            <SearchRow query={query} showAll={showAll} onQuery={setQuery} onShowAll={setShowAll} />
            <div className="df-list-scroll">
              <ul>
                {listRows.map((card) => {
                  const onShow = card.eventKey === eventKey && card.eventContactId != null;
                  const selected = active?.contactId === card.contactId;
                  const idLabel = onShow ? contactIdLabel(card) : "not on this show";
                  return (
                    <li key={card.contactId} id={`df-row-${card.contactId}`}>
                      <button
                        type="button"
                        className={selected ? "df-row on" : "df-row"}
                        disabled={!onShow}
                        aria-current={selected ? "true" : undefined}
                        onClick={() => openFromList(card)}
                      >
                        <span className="df-mini">{initials(card.fullName)}</span>
                        <span className="df-row-copy">
                          <strong>{card.fullName}</strong>
                          {card.title ? <small>{card.title}</small> : null}
                          {card.accountName ? <small>{card.accountName}</small> : null}
                          {idLabel ? <small>{idLabel}</small> : null}
                        </span>
                      </button>
                      {!onShow ? (
                        <button type="button" className="df-add" onClick={() => addSibling(card.contactId)}>
                          Add to show
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>
          <div
            className="df-list-split"
            role="separator"
            aria-orientation={stacked ? "horizontal" : "vertical"}
            aria-label={stacked ? "Resize contact list height" : "Resize contact list width"}
            aria-valuemin={stacked ? 120 : 220}
            aria-valuenow={Math.round(stacked ? listHeight : listWidth)}
            aria-valuemax={stacked ? 800 : 900}
            tabIndex={0}
            onPointerDown={onSplitPointerDown}
            onKeyDown={(event) => {
              const step = event.shiftKey ? 48 : 16;
              const shrink = stacked ? event.key === "ArrowUp" : event.key === "ArrowLeft";
              const grow = stacked ? event.key === "ArrowDown" : event.key === "ArrowRight";
              if (!shrink && !grow) return;
              event.preventDefault();
              nudgeSplit(shrink ? -step : step);
            }}
          />
          <section className="df-list-content" aria-label="Debrief profile">
            {active ? debriefProfile(active, index + 1) : <p className="df-empty">Choose a contact from the list.</p>}
          </section>
        </main>
      ) : (
        <main ref={stageWrapRef} className="df-stage-wrap">
          <div
            className="df-stage"
            role="region"
            aria-roledescription="carousel"
            aria-label="Tradeshow contacts"
            onPointerDown={(event) => {
              // A mouse drag selects text to copy. Swipe-to-change stays on touch and pen.
              if (event.pointerType === "mouse") return;
              const target = event.target instanceof Element ? event.target : null;
              pointer.current = {
                id: event.pointerId,
                x: event.clientX,
                y: event.clientY,
                locked: null,
                ignore: Boolean(target?.closest("textarea, input, select, button, a, [contenteditable]")),
              };
            }}
            onPointerMove={(event) => {
              const swipe = pointer.current;
              if (!swipe || swipe.id !== event.pointerId || swipe.ignore || swipe.locked) return;
              const dx = event.clientX - swipe.x;
              const dy = event.clientY - swipe.y;
              if (Math.hypot(dx, dy) < 10) return;
              swipe.locked = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
              if (swipe.locked === "x") event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerUp={(event) => {
              const swipe = pointer.current;
              pointer.current = null;
              if (!swipe || swipe.id !== event.pointerId || swipe.ignore || swipe.locked !== "x") return;
              const dx = event.clientX - swipe.x;
              const dy = event.clientY - swipe.y;
              if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
              go(dx < 0 ? 1 : -1);
            }}
            onPointerCancel={() => {
              pointer.current = null;
            }}
          >
            <button type="button" className="df-peek" disabled={!prev} onClick={() => go(-1)}>
              {prev ? <Peek card={prev} /> : null}
            </button>
            {loading ? (
              <p className="df-empty">Loading contacts from Intel…</p>
            ) : loadError && showCards.length === 0 ? (
              <p className="df-empty">{loadError}</p>
            ) : active ? (
              <div className="df-card-wrap">
                <button type="button" className="df-arrow left" aria-label="Previous contact" disabled={!prev} onClick={() => go(-1)}>
                  ‹
                </button>
                {debriefProfile(active, index + 1)}
                <button type="button" className="df-arrow right" aria-label="Next contact" disabled={!next} onClick={() => go(1)}>
                  ›
                </button>
              </div>
            ) : (
              <p className="df-empty">No live contacts with an Event Contact ID on this show.</p>
            )}
            <button type="button" className="df-peek" disabled={!next} onClick={() => go(1)}>
              {next ? <Peek card={next} /> : null}
            </button>
          </div>
          <div className="df-dots" aria-label={`Contact ${index + 1} of ${showCards.length}`}>
            {dots.map((dot) => (
              <button key={dot} type="button" className={dot === index ? "on" : ""} aria-label={`Contact ${dot + 1}`} onClick={() => setIndex(dot)} />
            ))}
          </div>
        </main>
      )}
      {accountModalId ? (
        <CompanyBrowserScreen
          variant="modal"
          eventKey={eventKey}
          campaignKey={eventByKey(eventKey).campaignKey}
          target={{ account_id: accountModalId }}
          actor={currentUserId}
          onClose={() => setAccountModalId(null)}
        />
      ) : null}
    </div>
  );
}

function SearchRow({
  query,
  showAll,
  onQuery,
  onShowAll,
}: {
  query: string;
  showAll: boolean;
  onQuery: (value: string) => void;
  onShowAll: (value: boolean) => void;
}) {
  return (
    <div className="df-search-row">
      <input
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder="Search accounts and contacts"
        aria-label="Search accounts and contacts"
      />
      <label className="df-show-all">
        <input type="checkbox" checked={showAll} onChange={(event) => onShowAll(event.target.checked)} />
        Show all
      </label>
    </div>
  );
}

function MobileNavStrip({
  index,
  total,
  onPrev,
  onNext,
  onJump,
}: {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onJump: (index: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const canMove = total > 1;

  function commit(raw: string) {
    const next = Number(raw);
    if (Number.isInteger(next) && next >= 1 && next <= total) onJump(next - 1);
    setOpen(false);
  }

  return (
    <div className="df-mobile-nav">
      <button type="button" className="df-mobile-step" aria-label="Previous contact" disabled={!canMove} onClick={onPrev}>
        ‹
      </button>
      {open ? (
        <form
          className="df-mobile-jump"
          onSubmit={(event) => {
            event.preventDefault();
            commit(draft);
          }}
        >
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={Math.max(total, 1)}
            aria-label="Jump to contact number"
            value={draft}
            autoFocus
            onChange={(event) => setDraft(event.target.value)}
            onBlur={(event) => {
              const next = event.relatedTarget;
              if (next instanceof Element && next.closest(".df-mobile-nav")) return;
              commit(draft);
            }}
          />
          <span>/ {total}</span>
        </form>
      ) : (
        <button
          type="button"
          className="df-mobile-count"
          aria-label={`Contact ${total === 0 ? 0 : index + 1} of ${total}. Jump to a contact.`}
          onClick={() => {
            setDraft(String(total === 0 ? 0 : index + 1));
            setOpen(true);
          }}
        >
          {total === 0 ? "0 / 0" : `${index + 1} / ${total}`}
        </button>
      )}
      <button type="button" className="df-mobile-step" aria-label="Next contact" disabled={!canMove} onClick={onNext}>
        ›
      </button>
    </div>
  );
}

function Peek({ card }: { card: DebriefCard }) {
  return (
    <span className="df-peek-card">
      <span className="df-mini">{initials(card.fullName)}</span>
      <strong>{card.fullName}</strong>
      <small>{card.accountName}</small>
    </span>
  );
}
